import React, { useRef, useState } from 'react';

import { authApi } from './authApi';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const EMPTY_PASSWORDS = {
  current_password: '',
  new_password: '',
  confirm_new_password: '',
};
const FIELDS = [
  { name: 'current_password', label: 'Current password', autoComplete: 'current-password' },
  { name: 'new_password', label: 'New password', autoComplete: 'new-password' },
  { name: 'confirm_new_password', label: 'Confirm new password', autoComplete: 'new-password' },
];

function messagesFor(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((item) => typeof item === 'string').join(' ');
  return '';
}

export default function ChangePasswordForm() {
  const { completePasswordChange } = useAuth();
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState({ ...EMPTY_PASSWORDS });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    setErrors({});
    const validation = {};
    FIELDS.forEach(({ name }) => {
      if (!passwords[name]) validation[name] = 'This field is required.';
    });
    if (passwords.new_password && passwords.new_password.length < 12) {
      validation.new_password = 'Use at least 12 characters.';
    }
    if (passwords.confirm_new_password && passwords.new_password !== passwords.confirm_new_password) {
      validation.confirm_new_password = 'The new passwords do not match.';
    }
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    try {
      const generation = await authApi.changePassword(passwords);
      setPasswords({ ...EMPTY_PASSWORDS });
      if (completePasswordChange(generation)) {
        navigate('/login', { replace: true, state: { passwordChanged: true } });
      }
    } catch (error) {
      const data = error?.response?.data;
      const fieldErrors = {};
      FIELDS.forEach(({ name }) => {
        const message = messagesFor(data?.[name]);
        if (message) fieldErrors[name] = message;
      });
      const general = messagesFor(data?.non_field_errors) || messagesFor(data?.detail);
      if (general) fieldErrors.general = general;
      if (!Object.keys(fieldErrors).length) {
        fieldErrors.general = 'Unable to change your password. Please try again.';
      }
      setErrors(fieldErrors);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="change-password-title">
      <h2 id="change-password-title" className="text-lg font-semibold">Change Password</h2>
      <p id="password-requirements" className="mt-2 text-sm text-slate-600">
        Use at least 12 characters. Avoid common passwords, entirely numeric passwords, and passwords similar to your account details.
      </p>
      <form aria-label="Change Password" className="mt-4 space-y-4" onSubmit={submit} aria-busy={submitting}>
        {FIELDS.map(({ name, label, autoComplete }) => (
          <div key={name}>
            <label htmlFor={name} className="block text-sm font-medium text-slate-700">{label}</label>
            <input
              id={name}
              name={name}
              type="password"
              autoComplete={autoComplete}
              value={passwords[name]}
              required
              disabled={submitting}
              aria-invalid={Boolean(errors[name])}
              aria-describedby={errors[name] ? name + '-error' : name === 'new_password' ? 'password-requirements' : undefined}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              onChange={(event) => {
                setPasswords((current) => ({ ...current, [name]: event.target.value }));
                setErrors((current) => ({ ...current, [name]: '', general: '' }));
              }}
            />
            {errors[name] && <p id={name + '-error'} role="alert" className="mt-1 text-sm text-red-700">{errors[name]}</p>}
          </div>
        ))}
        {errors.general && <p role="alert" className="text-sm text-red-700">{errors.general}</p>}
        <button type="submit" disabled={submitting} className="rounded bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-50">
          {submitting ? 'Changing password…' : 'Change password'}
        </button>
      </form>
    </section>
  );
}
